<script setup>
/* ─────────────────── imports ─────────────────── */
import { reactive, ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import Papa from 'papaparse';
import { toRefs } from 'vue';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

/* ─────────────────── emits / i18n ─────────────────── */
const emit = defineEmits(['submit', 'cancel', 'preview']);
const { t } = useI18n();
const K = 'CAMPAIGN.CSV.WHATSAPP.CREATE.FORM.'; // prefijo i18n

/* ─────────────────── store refs ─────────────────── */
const formState = {
  uiFlags: useMapGetter('campaignsWhatsApp/getUIFlags'),
  csvUiFlags: useMapGetter('campaignsCSVWhatsApp/getUIFlags'),
  inboxes: useMapGetter('inboxes/getWhatsAppInboxes'),
  getFilteredWhatsAppTemplates: useMapGetter(
    'inboxes/getFilteredWhatsAppTemplates'
  ),
  labels: useMapGetter('labels/getLabels'),
};

/* ─────────────────── local state ─────────────────── */
const initialState = {
  title: '',
  inboxId: null,
  selectedWhatsAppTemplate: null,
  scheduledAt: null,
  phoneNumber: '',
};
const state = reactive({ ...initialState });

const files = reactive({
  contactsFile: null,
  contactsFileName: '',
  headerMediaFile: null,
  headerMediaName: '',
});

const csvColumns = ref([]);
const headerFileNameError = ref(false);
const headerFileSizeError = ref(false);
const campaignLabels = ref([]);
const REQUIRED_CSV_COLUMNS = ['phone_number', 'status', 'error'];
const RESERVED_CSV_COLUMNS = ['phone_number', 'status', 'error', 'label'];
const bodyVariables = reactive([]); // [{sourceType,value}]
const buttonVariables = reactive([]); // [{type,dynamic,value,preview}]

/* ─────────────────── flags UI ─────────────────── */
const isCreating = computed(() => formState.csvUiFlags.value.isCreating);
const isPreviewing = computed(() => formState.uiFlags.value.isPreviewing);

/* ─────────────────── validations ─────────────────── */
const isPhoneValid = computed(() =>
  /^\+\d{6,15}$/.test(state.phoneNumber.trim())
);

// Custom validator for no spaces in URL
const noSpacesInUrl = value => {
  if (typeof value !== 'string') return true; // Allows non-string values, or handles cases where value might be empty
  return !/\s/.test(value);
};

const rules = {
  title: { required, minLength: minLength(1) },
  inboxId: { required },
  selectedWhatsAppTemplate: { required },
  contactsFile: { required },
  scheduledAt: { required },
};
const v$ = useVuelidate(rules, {
  ...toRefs(state), // ← mantiene la reactividad
  contactsFile: computed(() => files.contactsFile),
});

/* ─────────────────── helpers dependientes de inbox/template ─────────────────── */
const selectedInbox = computed(() =>
  formState.inboxes.value.find(inb => inb.id === state.inboxId)
);
const filteredTemplatesForInbox = computed(() => {
  if (!selectedInbox.value?.id) return [];
  return (
    formState.getFilteredWhatsAppTemplates.value(selectedInbox.value.id) || []
  );
});

const hasTemplates = computed(
  () => (filteredTemplatesForInbox.value?.length || 0) > 0
);

const hasNamedPH = tpl =>
  tpl?.components?.some(c =>
    c?.text
      ?.match(/{{(.*?)}}/g)
      ?.some(ph => !/^\d+$/.test(ph.replace(/[{}]/g, '')))
  );

const whatsappTemplateOptions = computed(
  () =>
    filteredTemplatesForInbox.value
      ?.filter(tpl => !hasNamedPH(tpl))
      .map(tpl => ({ value: tpl.id, label: tpl.name })) ?? []
);

const selectedTemplate = computed(() =>
  filteredTemplatesForInbox.value?.find(
    tpl => tpl.id === state.selectedWhatsAppTemplate
  )
);

/* preview concatenado (HEADER + BODY) */
const templatePreview = computed(() => {
  if (!selectedTemplate.value) return '';
  return selectedTemplate.value.components
    .filter(comp => ['HEADER', 'BODY'].includes(comp.type) && comp.text)
    .map(comp => comp.text)
    .join('\n');
});

/* footer plain text */
const footerText = computed(
  () =>
    selectedTemplate.value?.components?.find(c => c.type === 'FOOTER')?.text ||
    ''
);

/* header helpers */
const selectedHeader = computed(
  () =>
    selectedTemplate.value?.components?.find(c => c.type === 'HEADER') ?? null
);
const showMediaHeader = computed(() =>
  ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(selectedHeader.value?.format || '')
);
const headerAcceptMime = fmt =>
  ({
    IMAGE: 'image/jpeg,image/png',
    VIDEO: 'video/mp4',
    DOCUMENT: 'application/pdf',
  })[fmt] || '*/*';

const MEDIA_MAX_SIZE_MB = { IMAGE: 5, VIDEO: 16, DOCUMENT: 100 };
const BYTES_PER_MB = 1024 * 1024;

/* placeholders completos? */
const areBodyVarsFilled = computed(() =>
  bodyVariables.every(v => v.sourceType !== 'text' || v.value.trim())
);
const areButtonVarsFilled = computed(() =>
  buttonVariables.every(b => {
    // If it's dynamic and a URL, check for spaces and if it's filled
    if (b.type === 'URL' && b.dynamic) {
      return b.value.trim() !== '' && noSpacesInUrl(b.value);
    }
    // For other dynamic types, just check if it's filled
    if (b.dynamic) {
      return b.value.trim() !== '';
    }
    // If not dynamic, it's considered filled
    return true;
  })
);

// For preview: only allow text-only variables
const areBodyVarsTextOnly = computed(() =>
  bodyVariables.every(v => v.sourceType === 'text')
);

/* Validation for header media file (if required) */
const isHeaderMediaFileValid = computed(() => {
  if (showMediaHeader.value) {
    return files.headerMediaFile !== null;
  }
  return true;
});

/* ─────────────────── disable flags ─────────────────── */
const isCreateDisabled = computed(
  () =>
    v$.value.title.$invalid ||
    v$.value.inboxId.$invalid ||
    v$.value.selectedWhatsAppTemplate.$invalid ||
    v$.value.contactsFile.$invalid ||
    v$.value.scheduledAt.$invalid ||
    !areBodyVarsFilled.value ||
    !areButtonVarsFilled.value || // This now includes the no-spaces check for URL buttons
    !files.contactsFile ||
    !isHeaderMediaFileValid.value // Added validation for header media file
);
const isPreviewDisabled = computed(
  () =>
    !state.phoneNumber ||
    !isPhoneValid.value ||
    !state.inboxId ||
    !state.selectedWhatsAppTemplate ||
    !areBodyVarsFilled.value ||
    !areBodyVarsTextOnly.value ||
    !areButtonVarsFilled.value || // This now includes the no-spaces check for URL buttons
    !isHeaderMediaFileValid.value // Added validation for header media file
);

/* ─────────────────── CSV helpers ─────────────────── */
const validateHeaders = headers => {
  const lower = headers.map(h => h.trim().toLowerCase());
  const missing = REQUIRED_CSV_COLUMNS.filter(r => !lower.includes(r));
  return { valid: !missing.length, missing };
};

function handleContactsChange(e) {
  const file = e.target.files[0];
  if (!file) {
    files.contactsFile = null;
    files.contactsFileName = '';
    csvColumns.value = [];
    v$.value.contactsFile.$touch(); // Trigger validation for contactsFile
    return;
  }
  if (!file.name.toLowerCase().endsWith('.csv')) {
    useAlert(t(`${K}FILE.INVALID_FORMAT`));
    files.contactsFile = null; // Invalidate the file
    files.contactsFileName = '';
    csvColumns.value = [];
    v$.value.contactsFile.$touch(); // Trigger validation for contactsFile
    return;
  }

  files.contactsFile = file;
  files.contactsFileName = file.name;

  const reader = new FileReader();
  reader.onload = ev => {
    const parsed = Papa.parse(ev.target.result, {
      header: true,
      preview: 1,
      skipEmptyLines: true,
      transformHeader: h => (h ? h.trim() : h),
    });
    const { valid, missing } = validateHeaders(parsed.meta.fields || []);
    if (!valid) {
      useAlert(t(`${K}MISSING_COLUMNS`, { cols: missing.join(', ') }));
      files.contactsFile = null;
      files.contactsFileName = '';
      csvColumns.value = [];
      v$.value.contactsFile.$touch(); // Trigger validation for contactsFile
      return;
    }
    // Normalize headers and drop reserved columns from user-facing selector
    const normalized = (parsed.meta.fields || [])
      .filter(Boolean)
      .map(h => h.trim());
    csvColumns.value = normalized.filter(
      h => !RESERVED_CSV_COLUMNS.includes(h.toLowerCase())
    );
    v$.value.contactsFile.$touch(); // Trigger validation for contactsFile
  };
  reader.readAsText(file);
}
/** ----- PREVIEW: armar datos y emitir ----- */
function handleSendPreview() {
  // Teléfono
  if (!isPhoneValid.value) {
    useAlert(t(`${K}PREVIEW_SECTION.ERROR_PLUS`));
    return;
  }

  // Placeholders
  if (!areBodyVarsFilled.value) {
    useAlert(t(`${K}TEMPLATE.BODY.ERROR_PLACEHOLDERS`));
    return;
  }
  if (!areBodyVarsTextOnly.value) {
    useAlert(t(`${K}TEMPLATE.BODY.ERROR_TEXT_ONLY`));
    return;
  }
  if (!areButtonVarsFilled.value) {
    useAlert(t(`${K}TEMPLATE.BUTTONS.ERROR_EMPTY_URL`));
    return;
  }

  // Archivo de header (si corresponde)
  if (showMediaHeader.value && !files.headerMediaFile) {
    useAlert(t(`${K}TEMPLATE.MEDIA.ERROR_FILE_REQUIRED`));
    return;
  }

  /* ---------- datos que necesita el back-end ---------- */
  const previewData = {
    inboxId: state.inboxId,
    phoneNumber: state.phoneNumber.trim(),
    template: selectedTemplate.value,
    headerMediaFile: files.headerMediaFile,
    bodyVariables,
    buttonVariables,
  };

  /* ---------- emitimos al contenedor ---------- */
  emit('preview', previewData);
}

/* ─────────────────── header media file ─────────────────── */
function handleHeaderChange(e) {
  headerFileNameError.value = false;
  headerFileSizeError.value = false;
  const f = e.target.files[0];
  if (!f) {
    files.headerMediaFile = null;
    files.headerMediaName = '';
    return;
  }

  const base = f.name.replace(/\.[^.]+$/, '');
  if (base.length > 25) {
    headerFileNameError.value = true;
    e.target.value = '';
    files.headerMediaFile = null;
    files.headerMediaName = '';
    return;
  }

  const format = selectedHeader.value?.format;
  const maxMB = MEDIA_MAX_SIZE_MB[format] || 16;
  if (f.size > maxMB * BYTES_PER_MB) {
    headerFileSizeError.value = true;
    e.target.value = '';
    files.headerMediaFile = null;
    files.headerMediaName = '';
    return;
  }

  files.headerMediaFile = f;
  files.headerMediaName = f.name;
}

/* ─────────────────── parse placeholders ─────────────────── */
function parseTemplateVariables() {
  bodyVariables.splice(0);
  buttonVariables.splice(0);

  if (!selectedTemplate.value) return;

  /* BODY variables */
  const body = selectedTemplate.value.components.find(c => c.type === 'BODY');
  body?.text
    ?.match(/{{\d+}}/g)
    ?.forEach(() => bodyVariables.push({ sourceType: 'text', value: '' }));

  /* BUTTON variables + preview */
  const btnComp = selectedTemplate.value.components.find(
    c => c.type === 'BUTTONS'
  );
  btnComp?.buttons?.forEach(btn => {
    const hasPH = /{{\d+}}/.test(btn.url ?? btn.phone_number ?? '');
    if (btn.type === 'COPY_CODE') {
      buttonVariables.push({
        type: 'COPY_CODE',
        dynamic: true,
        value: '',
        preview: btn.text || 'Copy Code',
      });
    } else if (btn.type === 'URL' && hasPH) {
      buttonVariables.push({
        type: 'URL',
        dynamic: true,
        value: '',
        preview: btn.url,
      });
    } else if (btn.type === 'PHONE_NUMBER' && hasPH) {
      buttonVariables.push({
        type: 'PHONE_NUMBER',
        dynamic: true,
        value: '',
        preview: btn.phone_number,
      });
    }
  });
}

/* watchers */
watch(
  [
    () => state.selectedWhatsAppTemplate,
    () => state.inboxId,
    () => formState.inboxes.value,
  ],
  parseTemplateVariables,
  { immediate: true }
);

watch(
  () => ({
    ...state,
    contactsFile: files.contactsFile,
    headerMediaFile: files.headerMediaFile,
    bodyVars: bodyVariables.map(v => `${v.sourceType}:${v.value}`),
    buttonVars: buttonVariables.map(b => b.value),
  }),
  () => v$.value.$touch(),
  { deep: true }
);

/* helper para renderizar texto de preview sin usar regex en template */
function renderButtonPreview(raw) {
  // Mostramos el texto tal cual, conservando placeholders ({{1}}, etc.)
  return raw || '';
}

const labelOptions = computed(() =>
  (formState.labels.value || []).map(l => ({ value: l.title, label: l.title }))
);

/* helpers */
const toUTC = l => (l ? new Date(l).toISOString() : null);

async function handleSubmit() {
  const result = await v$.value.$validate();
  if (!result) return;

  if (!areBodyVarsFilled.value) {
    useAlert(t(`${K}TEMPLATE.BODY.ERROR_PLACEHOLDERS`));
    return;
  }

  if (!areButtonVarsFilled.value) {
    useAlert(t(`${K}TEMPLATE.BUTTONS.ERROR_EMPTY_URL`));
    return;
  }

  if (showMediaHeader.value && !files.headerMediaFile) {
    useAlert(t(`${K}TEMPLATE.MEDIA.ERROR_FILE_REQUIRED`));
    return;
  }

  const fd = new FormData();
  fd.append('title', state.title);
  fd.append('inbox_id', state.inboxId);
  fd.append('template', JSON.stringify(selectedTemplate.value));
  fd.append('scheduled_at', toUTC(state.scheduledAt));
  fd.append('csv_file', files.contactsFile);
  fd.append('original_csv_filename', files.contactsFileName);
  fd.append('body_variables', JSON.stringify(bodyVariables));
  fd.append('button_variables', JSON.stringify(buttonVariables));
  campaignLabels.value.forEach(label => fd.append('campaign_labels[]', label));
  if (files.headerMediaFile)
    fd.append('headerMediaFile', files.headerMediaFile);

  emit('submit', fd);
}

const handleCancel = () => emit('cancel');
</script>

<template>
  <div class="overflow-y-auto max-h-[80vh] p-6 space-y-6">
    <div class="space-y-1">
      <label class="block text-sm font-medium text-n-slate-12">
        {{ t(`${K}FILE.LABEL`) }}
      </label>

      <label
        for="contacts-upload"
        class="flex items-center justify-center gap-2 px-4 py-2 bg-n-alpha-3 border border-dashed rounded-md text-sm cursor-pointer hover:bg-n-alpha-4"
        :class="v$.contactsFile.$error ? 'border-red-500' : 'border-n-slate-7'"
      >
        <i class="i-lucide-upload w-4 h-4" />
        <span>{{ files.contactsFileName || t(`${K}FILE.CHOOSE`) }}</span>
      </label>

      <input
        id="contacts-upload"
        type="file"
        accept=".csv"
        class="sr-only"
        @change="handleContactsChange"
      />
      <p v-if="v$.contactsFile.$error" class="text-xs text-red-500 mt-1">
        {{ t(`${K}FILE.ERROR`) }}
      </p>
    </div>

    <div class="grid gap-6 md:grid-cols-2">
      <div class="space-y-6">
        <Input
          v-model="state.title"
          :label="t(`${K}TITLE.LABEL`)"
          :placeholder="t(`${K}TITLE.PLACEHOLDER`)"
          :message="v$.title.$error ? t(`${K}TITLE.ERROR`) : ''"
          :message-type="v$.title.$error ? 'error' : 'info'"
        />

        <div>
          <label class="block mb-1 text-sm font-medium text-n-slate-12">
            {{ t(`${K}WHATSAPP_TEMPLATE.LABEL`) }}
          </label>
          <ComboBox
            v-model="state.selectedWhatsAppTemplate"
            :options="whatsappTemplateOptions"
            :placeholder="t(`${K}WHATSAPP_TEMPLATE.PLACEHOLDER`)"
            :has-error="v$.selectedWhatsAppTemplate.$error"
            :message="
              v$.selectedWhatsAppTemplate.$error
                ? t(`${K}WHATSAPP_TEMPLATE.ERROR`)
                : ''
            "
            :message-type="
              v$.selectedWhatsAppTemplate.$error ? 'error' : 'info'
            "
            :disabled="!state.inboxId || !hasTemplates"
          />
          <p
            v-if="state.inboxId && !hasTemplates"
            class="text-xs text-red-500 mt-1"
          >
            {{ t(`${K}INBOX.NO_TEMPLATES`) }}
          </p>
        </div>

        <div v-if="templatePreview">
          <p class="mb-1 text-sm font-medium text-n-slate-12">
            {{ t(`${K}TEMPLATE.PREVIEW_TITLE`) }}
          </p>
          <pre
            class="whitespace-pre-line rounded-lg border border-n-slate-7 p-3 text-sm text-n-slate-11 overflow-x-auto"
            >{{ templatePreview }}</pre
          >
        </div>

        <div v-if="footerText">
          <h4 class="text-sm font-semibold text-n-slate-12 mb-2">
            {{ t(`${K}TEMPLATE.FOOTER.HEADER_SECTION`) }}
          </h4>
          <div class="rounded-lg border border-n-slate-7 p-3 bg-n-alpha-2">
            <div class="flex items-start gap-2">
              <p class="whitespace-pre-line text-sm text-n-slate-11">
                {{ footerText }}
              </p>
            </div>
          </div>
        </div>

        <div v-if="bodyVariables.length" class="space-y-3">
          <h4 class="text-sm font-semibold text-n-slate-12">
            {{ t(`${K}TEMPLATE.BODY.HEADER_SECTION`) }}
          </h4>

          <div v-for="(v, idx) in bodyVariables" :key="idx">
            <div
              class="flex items-center gap-2 bg-n-alpha-3 border rounded-md p-3"
              :class="
                v.sourceType === 'text' && !v.value.trim()
                  ? 'border-red-500'
                  : 'border-n-slate-8'
              "
            >
              <span
                class="w-6 h-6 flex items-center justify-center rounded-full text-xs font-semibold text-n-slate-11 bg-n-solid-3"
              >
                {{ idx + 1 }}
              </span>

              <ComboBox
                v-model="v.sourceType"
                class="md:w-1/3 flex-shrink-0"
                :options="[
                  { value: 'text', label: 'Text' },
                  ...csvColumns.map(c => ({ value: c, label: c })),
                ]"
                @update:model-value="
                  val => {
                    if (val !== 'text') v.value = '';
                  }
                "
              />

              <input
                v-if="v.sourceType === 'text'"
                v-model="v.value"
                class="flex-1 text-sm border rounded px-2 py-1 bg-transparent placeholder-n-slate-11 focus:outline-none"
                :class="!v.value.trim() ? 'border-red-500' : 'border-n-slate-7'"
                :placeholder="t(`${K}TEMPLATE.BODY.VARIABLE_PLACE_HOLDER`)"
              />
            </div>
            <p
              v-if="v.sourceType === 'text' && !v.value.trim()"
              class="text-xs text-red-500 mt-1"
            >
              {{ t(`${K}TEMPLATE.BODY.ERROR_PLACEHOLDERS_REQUIRED`) }}
            </p>
          </div>
        </div>

        <Input
          v-model="state.phoneNumber"
          :label="t(`${K}PREVIEW_SECTION.PHONE_LABEL`)"
          :placeholder="t(`${K}PREVIEW_SECTION.PLACEHOLDER`)"
          :message="
            state.phoneNumber && !isPhoneValid
              ? t(`${K}PREVIEW_SECTION.ERROR_PLUS`)
              : ''
          "
          :message-type="state.phoneNumber && !isPhoneValid ? 'error' : 'info'"
        />
      </div>

      <div class="space-y-6">
        <div>
          <label class="block mb-1 text-sm font-medium text-n-slate-12">
            {{ t(`${K}INBOX.LABEL`) }}
          </label>
          <ComboBox
            v-model="state.inboxId"
            :options="
              formState.inboxes.value?.map(i => ({
                value: i.id,
                label: i.name,
              })) ?? []
            "
            :placeholder="t(`${K}INBOX.PLACEHOLDER`)"
            :has-error="v$.inboxId.$error"
            :message="v$.inboxId.$error ? t(`${K}INBOX.ERROR`) : ''"
            :message-type="v$.inboxId.$error ? 'error' : 'info'"
          />
        </div>

        <div v-if="showMediaHeader" class="space-y-2">
          <h4 class="text-sm font-semibold text-n-slate-12">
            {{ t(`${K}TEMPLATE.MEDIA.HEADER_SECTION`) }}
          </h4>
          <label
            for="header-upload"
            class="flex items-center justify-center gap-2 px-4 py-2 bg-n-alpha-3 border border-dashed rounded-md text-sm cursor-pointer hover:bg-n-alpha-4"
            :class="
              !isHeaderMediaFileValid ? 'border-red-500' : 'border-n-slate-7'
            "
          >
            <i class="i-lucide-upload w-4 h-4" />
            <span>
              {{
                files.headerMediaName ||
                t(
                  {
                    IMAGE: `${K}TEMPLATE.MEDIA.UPLOAD_IMAGE`,
                    VIDEO: `${K}TEMPLATE.MEDIA.UPLOAD_VIDEO`,
                    DOCUMENT: `${K}TEMPLATE.MEDIA.UPLOAD_DOCUMENT`,
                  }[selectedHeader.format]
                )
              }}
            </span>
          </label>
          <input
            id="header-upload"
            type="file"
            class="sr-only"
            :accept="headerAcceptMime(selectedHeader.format)"
            @change="handleHeaderChange"
          />
          <p v-if="headerFileNameError" class="text-xs text-red-500 mt-1">
            {{ t(`${K}TEMPLATE.MEDIA.FILENAME_TOO_LONG`) }}
          </p>
          <p v-if="headerFileSizeError" class="text-xs text-red-500 mt-1">
            {{
              t(`${K}TEMPLATE.MEDIA.FILE_TOO_LARGE`, {
                max: MEDIA_MAX_SIZE_MB[selectedHeader.format] || 16,
              })
            }}
          </p>
          <p
            v-else-if="!isHeaderMediaFileValid"
            class="text-xs text-red-500 mt-1"
          >
            {{ t(`${K}TEMPLATE.MEDIA.ERROR_FILE_REQUIRED`) }}
          </p>
        </div>

        <Input
          v-model="state.scheduledAt"
          type="datetime-local"
          :min="
            new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
              .toISOString()
              .slice(0, 16)
          "
          :label="t(`${K}SCHEDULED_AT.LABEL`)"
          :message="v$.scheduledAt.$error ? t(`${K}SCHEDULED_AT.ERROR`) : ''"
          :message-type="v$.scheduledAt.$error ? 'error' : 'info'"
        />

        <div v-if="buttonVariables.length" class="space-y-3">
          <h4 class="text-sm font-semibold text-n-slate-12">
            {{ t(`${K}TEMPLATE.BUTTONS.HEADER_SECTION`) }}
          </h4>

          <div
            v-for="(btn, idx) in buttonVariables"
            :key="idx"
            class="flex flex-col gap-2 bg-n-alpha-3 border rounded-md p-3"
            :class="
              (btn.dynamic && !btn.value.trim()) ||
              (btn.type === 'URL' && btn.dynamic && !noSpacesInUrl(btn.value))
                ? 'border-red-500'
                : 'border-n-slate-8'
            "
          >
            <p class="text-xs text-n-slate-11">
              {{ `Botón ${btn.type}` }}:
              <span class="font-medium">{{
                renderButtonPreview(btn.preview)
              }}</span>
            </p>

            <input
              v-model="btn.value"
              class="border rounded px-2 py-1 text-sm"
              :class="
                (btn.dynamic && !btn.value.trim()) ||
                (btn.type === 'URL' && btn.dynamic && !noSpacesInUrl(btn.value))
                  ? 'border-red-500'
                  : 'border-n-slate-7'
              "
              :placeholder="t(`${K}TEMPLATE.BUTTONS.VARIABLE_PLACE_HOLDER`)"
            />
            <p
              v-if="btn.dynamic && !btn.value.trim()"
              class="text-xs text-red-500 mt-1"
            >
              {{ t(`${K}TEMPLATE.BUTTONS.ERROR_EMPTY_VARIABLE`) }}
            </p>
            <p
              v-if="
                btn.type === 'URL' && btn.dynamic && !noSpacesInUrl(btn.value)
              "
              class="text-xs text-red-500 mt-1"
            >
              {{ t(`${K}TEMPLATE.BUTTONS.ERROR_URL_NO_SPACES`) }}
              <span class="font-bold">{{
                t(`${K}TEMPLATE.BUTTONS.ERROR_URL_EXAMPLE`)
              }}</span>
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="space-y-2">
      <div class="flex items-center gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t(`${K}LABELS.LABEL`) }}
        </label>
        <div class="relative group">
          <button
            type="button"
            class="text-n-slate-9 hover:text-n-slate-11 flex items-center"
          >
            <i class="i-lucide-circle-help w-4 h-4" />
          </button>
          <div
            class="absolute bottom-full left-0 mb-2 w-72 bg-n-solid-3 border border-n-slate-6 rounded-md p-3 text-xs text-n-slate-11 shadow-lg invisible group-hover:visible z-10"
          >
            {{ t(`${K}LABELS.TOOLTIP`) }}
          </div>
        </div>
      </div>
      <TagMultiSelectComboBox
        v-model="campaignLabels"
        :options="labelOptions"
        :placeholder="t(`${K}LABELS.PLACEHOLDER`)"
        :search-placeholder="t(`${K}LABELS.SEARCH_PLACEHOLDER`)"
        :empty-state="t(`${K}LABELS.EMPTY_STATE`)"
      />
    </div>

    <div class="flex items-center justify-between pt-4">
      <div class="flex items-center gap-4">
        <Button
          variant="faded"
          color="slate"
          :label="t('CAMPAIGN.CSV.WHATSAPP.CREATE.CANCEL_BUTTON_TEXT')"
          @click="handleCancel"
        />
        <div class="flex items-center gap-2">
          <a
            href="https://docs.google.com/spreadsheets/d/1OI_YRW76fwdOfuKJJi3unq7nXO6YG2tCZqvW5uZTLX8/edit?usp=sharing"
            target="_blank"
            rel="noopener noreferrer"
            class="text-n-brand text-sm underline"
          >
            {{ t('CAMPAIGN.CSV.WHATSAPP.CREATE.DOWNLOAD_SAMPLE_CSV') }}
          </a>
          <div class="flex items-center gap-1">
            <a
              href="https://docs.google.com/document/d/1hTNCbnXMFAC87S6SYTUWc9DHryuyOrQRI1jdAhvjPKI/edit?usp=sharing"
              target="_blank"
              rel="noopener noreferrer"
              class="text-n-brand text-sm underline"
            >
              {{ t('CAMPAIGN.CSV.WHATSAPP.CREATE.HOW_TO_USE') }}
            </a>
            <svg
              class="w-4 h-4 text-n-slate-11"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
              :title="t('CAMPAIGN.CSV.WHATSAPP.CREATE.HELP_TOOLTIP')"
            >
              <path
                fill-rule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
        </div>
      </div>

      <div class="flex gap-2">
        <Button
          :label="t(`${K}PREVIEW_SECTION.BUTTON_LABEL`)"
          :disabled="isPreviewing || isPreviewDisabled"
          :is-loading="isPreviewing"
          @click="handleSendPreview"
        />
        <Button
          :label="t(`${K}BUTTONS.CREATE`)"
          :disabled="isCreating || isCreateDisabled"
          :is-loading="isCreating"
          @click="handleSubmit"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}
</style>
